import { Box, Link, Stack, Typography } from '@mui/material';

import {
    ensureAbsoluteUrl,
    formatDate,
    shipmentMonitoringCellTypographySx,
    shipmentMonitoringStackCellRootSx,
    formatAirAliasQuantityWithUnit,
} from './shipmentMonitoringDefShared';
import { shipmentMonitoringClosureColumns } from './shipmentMonitoringClosureColumns';
import { getShipmentMonitoringIlStatusColumn } from './shipmentMonitoringIlStatusColumn';

export const getShipmentMonitoringAirImportColumns = (rows) => {
    return [
        {
            field: 'il_opus_reference_id',
            headerName: 'OPUS#',
            width: 75,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                return (
                    <Typography sx={shipmentMonitoringCellTypographySx}>
                        {p.value || p.row.il_opus_reference_id}
                    </Typography>
                );
            },
        },
        {
            field: 'il_trade_partner_id_client',
            headerName: 'Client',
            width: 70,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                return (
                    <Typography sx={shipmentMonitoringCellTypographySx}>
                        {p.value || p.row.il_trade_partner_id_client}
                    </Typography>
                );
            },
        },
        {
            field: 'il_trade_partner_id_shipper',
            headerName: 'Shipper',
            width: 70,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                const lines = (p.row.il_air_import_hbl ?? []).map((hbl) => hbl.il_trade_partner_id_shipper).join('\n');
                return <Typography sx={shipmentMonitoringCellTypographySx}>{lines}</Typography>;
            },
        },
        {
            field: 'il_trade_partner_id_consignee',
            headerName: 'Consignee',
            width: 70,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                const lines = (p.row.il_air_import_hbl ?? [])
                    .map((hbl) => hbl.il_trade_partner_id_consignee /* 필요 시 .name 등 */)
                    .join('\n');
                return <Typography sx={shipmentMonitoringCellTypographySx}>{lines}</Typography>;
            },
        },
        {
            field: 'mawb',
            headerName: 'MAWB#',
            width: 105,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => <Typography sx={shipmentMonitoringCellTypographySx}>{p.value ?? ''}</Typography>,
        },
        {
            field: 'hawb',
            headerName: 'HAWB#',
            width: 95,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => (
                <Stack sx={shipmentMonitoringStackCellRootSx}>
                    {(p.row.il_air_import_hbl ?? []).map((hbl, i) => {
                        const text = hbl.hawb ?? '';
                        const href = (hbl.hawb_link && String(hbl.hawb_link).trim()) || '';
                        const absoluteUrl = ensureAbsoluteUrl(href);
                        return absoluteUrl ? (
                            <Link
                                key={hbl.id ?? i}
                                href={absoluteUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                component="a"
                                sx={{
                                    ...shipmentMonitoringCellTypographySx,
                                    color: 'primary.main',
                                    textDecoration: 'underline',
                                }}
                            >
                                {text || 'View'}
                            </Link>
                        ) : (
                            <Typography key={hbl.id ?? i} sx={shipmentMonitoringCellTypographySx}>
                                {text}
                            </Typography>
                        );
                    })}
                </Stack>
            ),
        },
        {
            field: 'alias',
            headerName: 'Alias#',
            width: 75,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                const lines = (p.row.il_air_import_hbl_alias ?? []).map((hbl) => hbl.alias).join('\n');
                return <Typography sx={shipmentMonitoringCellTypographySx}>{lines}</Typography>;
            },
        },
        {
            field: 'il_opus_vessel_id',
            headerName: 'Flight#',
            width: 70,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                const lines = (p.row.il_air_import_hbl_alias ?? []).map((hbl) => hbl.il_opus_vessel_id).join('\n');
                return <Typography sx={shipmentMonitoringCellTypographySx}>{lines}</Typography>;
            },
        },
        {
            field: 'volume',
            headerName: 'Vol',
            width: 72,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                const lines = (p.row.il_air_import_hbl_alias ?? [])
                    .map((alias) => formatAirAliasQuantityWithUnit(alias))
                    .filter(Boolean)
                    .join('\n');
                return <Typography sx={shipmentMonitoringCellTypographySx}>{lines || ' '}</Typography>;
            },
        },
        {
            field: 'il_location_id_aol',
            headerName: 'AOL',
            width: 70,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                return (
                    <Typography sx={shipmentMonitoringCellTypographySx}>
                        {p.value?.code || p.row.il_location_id_aol?.code}
                    </Typography>
                );
            },
        },
        {
            field: 'il_location_id_aod',
            headerName: 'AOD',
            width: 70,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                return (
                    <Typography sx={shipmentMonitoringCellTypographySx}>
                        {p.value?.code || p.row.il_location_id_aod?.code}
                    </Typography>
                );
            },
        },
        {
            field: 'il_location_id_faod',
            headerName: 'F. DEST',
            width: 70,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                return (
                    <Typography sx={shipmentMonitoringCellTypographySx}>
                        {p.value?.code || p.row.il_location_id_faod?.ncodeame}
                    </Typography>
                );
            },
        },
        {
            field: 'il_location_id_deliver_to',
            headerName: 'Delivery Location',
            width: 100,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <Box sx={{ textAlign: 'center', lineHeight: 1.15 }}>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Delivery
                    </Box>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Location
                    </Box>
                </Box>
            ),
            renderCell: (p) => {
                const lines = (p.row.il_air_import_hbl_alias ?? [])
                    .map((cntr) => cntr.il_location_id_deliver_to)
                    .join('\n');
                return <Typography sx={shipmentMonitoringCellTypographySx}>{lines}</Typography>;
            },
        },

        {
            field: 'etd',
            headerName: 'ETD',
            width: 50,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                return <Typography sx={shipmentMonitoringCellTypographySx}>{formatDate(p.row.etd) || ' '}</Typography>;
            },
        },
        {
            field: 'eta',
            headerName: 'ETA',
            width: 50,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                return <Typography sx={shipmentMonitoringCellTypographySx}>{formatDate(p.row.eta) || ' '}</Typography>;
            },
        },
        {
            field: 'feta',
            headerName: 'F.ETA',
            width: 50,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                return <Typography sx={shipmentMonitoringCellTypographySx}>{formatDate(p.row.feta) || ' '}</Typography>;
            },
        },
        {
            field: 'pre_alert',
            headerName: 'Pre Alert',
            width: 50,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <Box sx={{ textAlign: 'center', lineHeight: 1.15 }}>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Pre
                    </Box>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Alert
                    </Box>
                </Box>
            ),
            renderCell: (p) => {
                const lines = (p.row.il_air_import_hbl ?? []).map((hbl) => hbl.pre_alert).join('\n');
                return <Typography sx={shipmentMonitoringCellTypographySx}>{lines}</Typography>;
            },
        },
        {
            field: 'air_freight',
            headerName: 'Collect Charge',
            width: 70,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <Box sx={{ textAlign: 'center', lineHeight: 1.15 }}>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Collect
                    </Box>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Charge
                    </Box>
                </Box>
            ),
            renderCell: (p) => {
                return (
                    <Link
                        key={p.row.id}
                        href={p.row.air_freight_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        component="a"
                        sx={{
                            ...shipmentMonitoringCellTypographySx,
                            color: 'primary.main',
                            textDecoration: 'underline',
                        }}
                    >
                        {p.row.air_freight ? p.row.air_freight : ''} <br />
                        {p.row.paid_on ? formatDate(p.row.paid_on) : ''}
                    </Link>
                );
            },
        },
        {
            field: 'import_service_fee',
            headerName: 'Import Service Fee',
            width: 60,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <Box sx={{ textAlign: 'center', lineHeight: 1.15 }}>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Import
                    </Box>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Service Fee
                    </Box>
                </Box>
            ),
            renderCell: (p) => {
                return (
                    <Stack sx={shipmentMonitoringStackCellRootSx}>
                        {(p.row.il_air_import_hbl ?? []).map((hbl, i) => {
                            const text = hbl.import_service_fee ?? '';
                            const href =
                                (hbl.import_service_fee_link && String(hbl.import_service_fee_link).trim()) || '';
                            const absoluteUrl = ensureAbsoluteUrl(href);
                            return absoluteUrl ? (
                                <Link
                                    key={hbl.id ?? i}
                                    href={absoluteUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    component="a"
                                    sx={{
                                        ...shipmentMonitoringCellTypographySx,
                                        color: 'primary.main',
                                        textDecoration: 'underline',
                                    }}
                                >
                                    {text || 'View'}
                                </Link>
                            ) : (
                                <Typography key={hbl.id ?? i} sx={shipmentMonitoringCellTypographySx}>
                                    {text}
                                </Typography>
                            );
                        })}
                    </Stack>
                );
            },
        },
        {
            field: 'arrival_notice',
            headerName: 'Arrival Notice',
            width: 60,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <Box sx={{ textAlign: 'center', lineHeight: 1.15 }}>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Arrival
                    </Box>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Notice
                    </Box>
                </Box>
            ),
            renderCell: (p) => {
                return (
                    <Stack sx={shipmentMonitoringStackCellRootSx}>
                        {(p.row.il_air_import_hbl ?? []).map((hbl, i) => {
                            const text = hbl.arrival_notice ?? '';
                            const href = (hbl.arrival_notice_link && String(hbl.arrival_notice_link).trim()) || '';
                            const absoluteUrl = ensureAbsoluteUrl(href);
                            return absoluteUrl ? (
                                <Link
                                    key={hbl.id ?? i}
                                    href={absoluteUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    component="a"
                                    sx={{
                                        ...shipmentMonitoringCellTypographySx,
                                        color: 'primary.main',
                                        textDecoration: 'underline',
                                    }}
                                >
                                    {text || 'View'}
                                </Link>
                            ) : (
                                <Typography key={hbl.id ?? i} sx={shipmentMonitoringCellTypographySx}>
                                    {text}
                                </Typography>
                            );
                        })}
                    </Stack>
                );
            },
        },
        {
            field: 'customs',
            headerName: 'Customs',
            width: 60,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => (
                <Stack sx={shipmentMonitoringStackCellRootSx}>
                    {(p.row.il_air_import_hbl_alias ?? []).map((cntr, i) => {
                        const text = cntr.customs ?? '';
                        const href = (cntr.customs_link && String(cntr.customs_link).trim()) || '';
                        const absoluteUrl = ensureAbsoluteUrl(href);
                        return absoluteUrl ? (
                            <Link
                                key={cntr.id ?? i}
                                href={absoluteUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                component="a"
                                sx={{
                                    ...shipmentMonitoringCellTypographySx,
                                    color: 'primary.main',
                                    textDecoration: 'underline',
                                }}
                            >
                                {text || 'View'}
                            </Link>
                        ) : (
                            <Typography key={cntr.id ?? i} sx={shipmentMonitoringCellTypographySx}>
                                {text}
                            </Typography>
                        );
                    })}
                </Stack>
            ),
        },
        {
            field: 'delivery_order',
            headerName: 'Delivery Order',
            width: 60,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <Box sx={{ textAlign: 'center', lineHeight: 1.15 }}>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Delivery
                    </Box>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Order
                    </Box>
                </Box>
            ),
            renderCell: (p) => (
                <Stack sx={shipmentMonitoringStackCellRootSx}>
                    {(p.row.il_air_import_hbl_alias ?? []).map((cntr, i) => {
                        const text = cntr.delivery_order ?? '';
                        const href = (cntr.delivery_order_link && String(cntr.delivery_order_link).trim()) || '';
                        const absoluteUrl = ensureAbsoluteUrl(href);
                        return absoluteUrl ? (
                            <Link
                                key={cntr.id ?? i}
                                href={absoluteUrl}
                                component="a"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    ...shipmentMonitoringCellTypographySx,
                                    color: 'primary.main',
                                    textDecoration: 'underline',
                                }}
                            >
                                {text || 'View'}
                            </Link>
                        ) : (
                            <Typography key={cntr.id ?? i} sx={shipmentMonitoringCellTypographySx}>
                                {text}
                            </Typography>
                        );
                    })}
                </Stack>
            ),
        },
        {
            field: 'trucker',
            headerName: 'Trucker',
            width: 60,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => {
                return (
                    <Stack sx={shipmentMonitoringStackCellRootSx}>
                        {(p.row.il_air_import_hbl_alias ?? []).map((cntr, i) => {
                            const text = cntr.il_trade_partner_id ?? '';
                            return (
                                <Typography key={cntr.id ?? i} sx={shipmentMonitoringCellTypographySx}>
                                    {text}
                                </Typography>
                            );
                        })}
                    </Stack>
                );
            },
        },
        getShipmentMonitoringIlStatusColumn({ statusHouseHeaderStyle: 'hawb' }),
        {
            field: 'proof',
            headerName: 'Proof Delivery',
            width: 60,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <Box sx={{ textAlign: 'center', lineHeight: 1.15 }}>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Proof
                    </Box>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Delivery
                    </Box>
                </Box>
            ),
            renderCell: (p) => (
                <Stack sx={shipmentMonitoringStackCellRootSx}>
                    {(p.row.il_air_import_hbl_alias ?? []).map((cntr, i) => {
                        const text = cntr.proof ?? '';
                        const date = cntr.proof_date ?? '';
                        const href = (cntr.proof_link && String(cntr.proof_link).trim()) || '';
                        const absoluteUrl = ensureAbsoluteUrl(href);
                        return absoluteUrl ? (
                            <Link
                                key={cntr.id ?? i}
                                href={absoluteUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                component="a"
                                sx={{
                                    ...shipmentMonitoringCellTypographySx,
                                    color: 'primary.main',
                                    textDecoration: 'underline',
                                }}
                            >
                                {text || ''} <br />
                                {date ? formatDate(date) : ''}
                            </Link>
                        ) : (
                            <Typography key={cntr.id ?? i} sx={shipmentMonitoringCellTypographySx}>
                                {text}
                            </Typography>
                        );
                    })}
                </Stack>
            ),
        },
        {
            field: 'invoice',
            headerName: 'Invoice',
            width: 60,
            align: 'center',
            headerAlign: 'center',
            renderCell: (p) => (
                <Stack sx={shipmentMonitoringStackCellRootSx}>
                    {(p.row.il_air_import_hbl ?? []).map((hbl, i) => {
                        const text = hbl.invoice ?? '';
                        const date = hbl.invoice_date ?? '';
                        const href = (hbl.invoice_link && String(hbl.invoice_link).trim()) || '';
                        const absoluteUrl = ensureAbsoluteUrl(href);
                        return absoluteUrl ? (
                            <Link
                                key={hbl.id ?? i}
                                href={absoluteUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                component="a"
                                sx={{
                                    ...shipmentMonitoringCellTypographySx,
                                    color: 'primary.main',
                                    textDecoration: 'underline',
                                }}
                            >
                                {text || ''} <br />
                                {date ? formatDate(date) : ''}
                            </Link>
                        ) : (
                            <Typography key={hbl.id ?? i} sx={shipmentMonitoringCellTypographySx}>
                                {text || ''} <br />
                                {date ? formatDate(date) : ''}
                            </Typography>
                        );
                    })}
                </Stack>
            ),
        },
        {
            field: 'crdr',
            headerName: 'CR/DR Note',
            width: 60,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <Box sx={{ textAlign: 'center', lineHeight: 1.15 }}>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        CR/DR
                    </Box>
                    <Box component="span" sx={{ display: 'block', fontWeight: 500 }}>
                        Note
                    </Box>
                </Box>
            ),
            renderCell: (p) => (
                <Stack sx={shipmentMonitoringStackCellRootSx}>
                    {(p.row.il_air_import_hbl ?? []).map((hbl, i) => {
                        const text = hbl.crdr ?? '';
                        const date = hbl.crdr_date ?? '';
                        const href = (hbl.crdr_link && String(hbl.crdr_link).trim()) || '';
                        const absoluteUrl = ensureAbsoluteUrl(href);
                        return absoluteUrl ? (
                            <Link
                                key={hbl.id ?? i}
                                href={absoluteUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                component="a"
                                sx={{
                                    ...shipmentMonitoringCellTypographySx,
                                    color: 'primary.main',
                                    textDecoration: 'underline',
                                }}
                            >
                                {text || ''} <br />
                                {date ? formatDate(date) : ''}
                            </Link>
                        ) : (
                            <Typography key={hbl.id ?? i} sx={shipmentMonitoringCellTypographySx}>
                                {text || ''} <br />
                                {date ? formatDate(date) : ''}
                            </Typography>
                        );
                    })}
                </Stack>
            ),
        },
        ...shipmentMonitoringClosureColumns,
    ];
};
